import { Mark, MarkType, ResolvedPos } from 'prosemirror-model'
import { Selection, Transaction } from 'prosemirror-state'
import { preserveSelection } from '../utils'
import { Command } from './types'
import { tableNodesSpec, schema, NON_FORMAT_MARKS } from 'prose-core'

export const _removeCertainAttrsFromSomeNodes = (tr: Transaction) => {
  const { selection } = tr

  for (let {
    $from: { pos: from },
    $to,
  } of selection.ranges) {
    tr.doc.nodesBetween(from, $to.pos, (node, pos) => {
      let newAttrs: { [k: string]: string | boolean | null } = {}
      switch (node.type) {
        case schema.nodes.table:
        case schema.nodes.table_row:
          newAttrs = { background: null }
          break
        case schema.nodes.table_header:
        case schema.nodes.table_cell:
          newAttrs = Object.fromEntries(
            Object.entries(tableNodesSpec.cellAttributes).flatMap(([k, v]) => {
              if (['cellWidth', 'cellHeight'].includes(k)) return []
              return [[k, v.default as any]]
            }),
          )
          break
      }
      if (Object.keys(newAttrs).length) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...newAttrs })
      }
    })
  }

  return tr
}

/**
 * Removes marks from current selection. When cursor selection, removes stored mark. Otherwise loops over selections ranges and removes mark on each range.
 *
 * When mark is a single mark, remove precisely that mark. When it is a mark type, remove all marks of that type.
 *
 * @param markType
 * @returns Command function
 */
export const _removeMark = (markType?: Mark | MarkType) => (tr: Transaction) => {
  const { empty, $cursor, ranges } = tr.selection as Selection & { $cursor?: ResolvedPos | null }
  if (empty && !$cursor) return tr
  if ($cursor) {
    if (markType) {
      tr.removeStoredMark(markType)
    } else {
      tr.setStoredMarks([])
    }
  } else {
    for (let i = 0; i < ranges.length; i++) {
      let { $from, $to } = ranges[i]

      if (markType) {
        tr.removeMark($from.pos, $to.pos, markType)
      } else {
        // if no markType is passed, we want to clear everything
        // except non-format related marks. Those should be kept.
        Object.entries(schema.marks).forEach(([key, value]) => {
          if (NON_FORMAT_MARKS.includes(key)) return tr

          tr.removeMark($from.pos, $to.pos, value)
        })
      }
    }
  }

  return tr
}

/**
 * Removes existing and Adds new mark of type given with optional attributes over current selection. If selection is cursor, operates on stored mark.
 *
 * @param markType
 * @param attrs
 * @returns Command function
 */

export const upsertMark: (markType: MarkType, attrs?: { [key: string]: any }) => Command = (markType, attrs) => {
  return (state, dispatch) => {
    const { empty, $cursor, ranges } = state.selection as Selection & { $cursor?: ResolvedPos | null },
      tr = state.tr
    if (empty && !$cursor) return false
    if (dispatch) {
      if ($cursor) {
        tr.removeStoredMark(markType)
        tr.addStoredMark(markType.create(attrs))
      } else {
        for (let i = 0; i < ranges.length; i++) {
          let { $from, $to } = ranges[i]
          let from = $from.pos,
            to = $to.pos

          tr.addMark(from, to, markType.create(attrs))
          preserveSelection(tr, state)
        }
      }
      dispatch(tr.scrollIntoView())
    }
    return true
  }
}
